import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, MessageSquareText, Sparkles, Wand2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAnalyticsApi } from '../../../common/hooks/use-analytics-api.hook';
import { AssistantInsights, Ranked } from '../../../common/models/assistant-insights.model';
import { AnalyticsPeriod, BarList, BarListItem, Thumb } from '../../../components/design-system/analytics';
import { KpiCard, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';

/** A vocabulary key ("cement-tile", "living-room") as words. */
const words = (value: string) => {
  const text = value.replace(/-/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const money = (value: Record<string, number>) => {
  const entries = Object.entries(value || {});
  if (!entries.length) return '—';
  return entries
    .map(([currency, amount]) => {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
      } catch {
        return `${formatNumber(amount)} ${currency}`;
      }
    })
    .join(' · ');
};

const DEMAND_FACETS = ['appearance', 'colorFamily', 'room', 'style', 'materialFamily', 'finish'] as const;

/**
 * What shoppers asked the visualizer's assistant for.
 *
 * The visualizer's own analytics say what visitors did. This says what they
 * wanted — in their words, before the catalogue answered — and what the
 * catalogue could not answer, which is the part a vendor can act on.
 */
export const AssistantInsightsPage: React.FC<{ period: AnalyticsPeriod }> = ({ period }) => {
  const { t } = useTranslation();
  const analytics = useAnalyticsApi();

  const { data, loading } = useLoader(
    () => analytics.getAssistantInsights(period.startDate, period.endDate),
    [period.startDate, period.endDate],
  );
  const d = data as AssistantInsights | null;

  const productItem = (r: Ranked): BarListItem => {
    const p = d?.products?.[r.value];
    return { key: r.value, label: p?.name || r.value.slice(0, 8), value: r.count, leading: <Thumb src={p?.thumb} alt="" size="sm" /> };
  };
  const rankedItems = (list: Ranked[] = [], label = (v: string) => v): BarListItem[] =>
    list.map((r) => ({ key: r.value, label: label(r.value), value: r.count }));

  const demand = useMemo(
    () => DEMAND_FACETS.filter((f) => d?.demand?.[f]?.length).map((f) => ({ facet: f, items: rankedItems(d!.demand[f], words) })),
    [d], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (loading && !d) {
    return (
      <div className="grid gap-4 md:grid-cols-4" data-testid="assistant-insights-loading">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }

  if (!d || (!d.opens && !d.queries && !d.quotes.count)) {
    return (
      <SectionCard testId="assistant-insights-empty">
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Sparkles className="size-8 text-primary" aria-hidden />
          <p className="text-base font-semibold">{t('VIZ_ANALYTICS.ASSISTANT.EMPTY_TITLE')}</p>
          <p className="max-w-md text-sm text-muted-foreground">{t('VIZ_ANALYTICS.ASSISTANT.EMPTY_BODY')}</p>
        </div>
      </SectionCard>
    );
  }

  const r = d.recommendations;

  return (
    <div className="flex flex-col gap-6" data-testid="assistant-insights">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label={t('VIZ_ANALYTICS.ASSISTANT.KPI_OPENS')} value={formatNumber(d.opens)} hint={t('VIZ_ANALYTICS.ASSISTANT.KPI_VISITORS', { count: d.visitors })} icon={<Sparkles className="size-4" />} />
        <KpiCard label={t('VIZ_ANALYTICS.ASSISTANT.KPI_QUERIES')} value={formatNumber(d.queries)} hint={t('VIZ_ANALYTICS.ASSISTANT.KPI_UNANSWERED', { count: d.zeroResultQueries.reduce((s, q) => s + q.count, 0) })} icon={<MessageSquareText className="size-4" />} />
        <KpiCard label={t('VIZ_ANALYTICS.ASSISTANT.KPI_TRIES')} value={formatNumber(r.tries)} hint={t('VIZ_ANALYTICS.ASSISTANT.KPI_SHOWN', { count: r.shown })} icon={<Wand2 className="size-4" />} />
        <KpiCard label={t('VIZ_ANALYTICS.ASSISTANT.KPI_QUOTES')} value={formatNumber(d.quotes.count)} hint={money(d.quotes.value)} icon={<FileText className="size-4" />} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard title={t('VIZ_ANALYTICS.ASSISTANT.ASKED_TITLE')} description={t('VIZ_ANALYTICS.ASSISTANT.ASKED_DESC')}>
          <BarList items={rankedItems(d.topQueries, (q) => `“${q}”`)} emptyText={t('VIZ_ANALYTICS.ASSISTANT.NOTHING_YET')} testId="assistant-top-queries" />
        </SectionCard>
        <SectionCard title={t('VIZ_ANALYTICS.ASSISTANT.UNANSWERED_TITLE')} description={t('VIZ_ANALYTICS.ASSISTANT.UNANSWERED_DESC')}>
          <BarList items={rankedItems(d.zeroResultQueries, (q) => `“${q}”`)} emptyText={t('VIZ_ANALYTICS.ASSISTANT.ALL_ANSWERED')} />
          {d.unmatchedWords.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">{t('VIZ_ANALYTICS.ASSISTANT.UNKNOWN_WORDS')}</p>
              <div className="flex flex-wrap gap-1.5">
                {d.unmatchedWords.map((w) => (
                  <Badge key={w.value} variant="secondary" className="font-normal">{w.value} <span className="ml-1 text-muted-foreground">{w.count}</span></Badge>
                ))}
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {demand.length > 0 && (
        <SectionCard title={t('VIZ_ANALYTICS.ASSISTANT.DEMAND_TITLE')} description={t('VIZ_ANALYTICS.ASSISTANT.DEMAND_DESC')}>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {demand.map(({ facet, items }) => (
              <div key={facet}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`VIZ_ANALYTICS.ASSISTANT.FACET_${facet.toUpperCase()}`)}</p>
                <BarList items={items.slice(0, 6)} />
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard title={t('VIZ_ANALYTICS.ASSISTANT.TASTE_TITLE')} description={t('VIZ_ANALYTICS.ASSISTANT.TASTE_DESC', { median: d.budget.median ?? '—' })}>
          {Object.keys(d.taste).length ? (
            <div className="grid gap-5 sm:grid-cols-2">
              {(['style', 'appearance', 'colorFamily', 'temperature'] as const).filter((f) => d.taste[f]?.length).map((facet) => (
                <div key={facet}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`VIZ_ANALYTICS.ASSISTANT.FACET_${facet.toUpperCase()}`)}</p>
                  <BarList
                    items={d.taste[facet].slice(0, 5).map((x) => ({ key: x.value, label: words(x.value), value: Math.round(x.share * 100) }))}
                    format={(v) => `${v}%`}
                    max={100}
                  />
                </div>
              ))}
            </div>
          ) : <p className="py-6 text-center text-sm text-muted-foreground">{t('VIZ_ANALYTICS.ASSISTANT.NOTHING_YET')}</p>}
        </SectionCard>
        <SectionCard title={t('VIZ_ANALYTICS.ASSISTANT.RECS_TITLE')} description={t('VIZ_ANALYTICS.ASSISTANT.RECS_DESC')}>
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {r.byList.slice(0, 4).map((l) => (
              <div key={l.list} className="rounded-lg border px-3 py-2">
                <p className="truncate text-xs text-muted-foreground">{t(`VIZ_ANALYTICS.ASSISTANT.LIST_${l.list.replace(/-/g, '_').toUpperCase()}`, { defaultValue: words(l.list) })}</p>
                <p className="text-lg font-semibold">{formatNumber(l.ctr * 100, 1)}%</p>
                <p className="text-[11px] text-muted-foreground">{t('VIZ_ANALYTICS.ASSISTANT.CLICKS_OF', { clicks: l.clicks, shown: l.shown })}</p>
              </div>
            ))}
          </div>
          <BarList items={r.topTried.map(productItem)} emptyText={t('VIZ_ANALYTICS.ASSISTANT.NOTHING_YET')} />
        </SectionCard>
      </div>

      <SectionCard
        title={t('VIZ_ANALYTICS.ASSISTANT.PROJECTS_TITLE')}
        description={t('VIZ_ANALYTICS.ASSISTANT.PROJECTS_DESC', {
          count: d.estimates.count,
          median: d.estimates.medianAreaM2 ?? '—',
          value: money(d.estimates.value),
        })}
        flush
      >
        {d.quotes.recent.length ? (
          <Table data-testid="assistant-quotes">
            <TableHeader>
              <TableRow>
                <TableHead>{t('VIZ_ANALYTICS.ASSISTANT.QUOTE_WHO')}</TableHead>
                <TableHead>{t('VIZ_ANALYTICS.ASSISTANT.QUOTE_WHEN')}</TableHead>
                <TableHead className="text-right">{t('VIZ_ANALYTICS.ASSISTANT.QUOTE_LINES')}</TableHead>
                <TableHead className="text-right">{t('VIZ_ANALYTICS.ASSISTANT.QUOTE_TOTAL')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.quotes.recent.map((q, i) => (
                <TableRow key={`${q.email}-${q.at}-${i}`}>
                  <TableCell>
                    <div className="font-medium">{q.name || q.email}</div>
                    <div className="text-xs text-muted-foreground">{[q.email, q.phone].filter(Boolean).join(' · ')}</div>
                    {q.note && <div className="mt-1 max-w-md text-xs italic text-muted-foreground">“{q.note}”</div>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{q.at ? new Date(q.at).toLocaleString() : '—'}</TableCell>
                  <TableCell className="text-right">{q.items}</TableCell>
                  <TableCell className="text-right font-medium">{q.total ? money({ [q.currency || 'EUR']: q.total }) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="px-6 py-8 text-center text-sm text-muted-foreground">{t('VIZ_ANALYTICS.ASSISTANT.NO_QUOTES')}</p>
        )}
      </SectionCard>
    </div>
  );
};

export default AssistantInsightsPage;
